//Dependencies............................................
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Checkbox, Form, Input, Modal, Select } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

//Components..............................................
import { BASE_URL } from "../../../utils/constants";
import { VehicleItem } from "../../../utils/interfaces";
import { NotificationInstance } from "antd/es/notification/interface";

interface Props {
  showModal: boolean;
  vehicle: VehicleItem;
  onClose: () => void;
  api: NotificationInstance
}

type CarMakerListResponse = {
  success: boolean;
  content: string[];
};

const EditVehicleModal: React.FC<Props> = ({
  showModal,
  vehicle,
  onClose,
  api
}: Props) => {

  const [form] = Form.useForm<VehicleItem>();

  const [manualManufacturer, setManualManufacturer] = useState(false);
  const [manualModel, setManualModel] = useState(false);
  const [modelList, setModelList] = useState<any[]>([]);
  const [makerList, setMakerList] = useState<any[]>([]);
  const [submittable, setSubmittable] = useState(false);

  const watchedValues = Form.useWatch([], form);
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, watchedValues]);

  const isDirty = React.useMemo(() => {
    if (!watchedValues || !vehicle) return false;
    return (
      watchedValues.manufacturer !== vehicle.manufacturer ||
      watchedValues.model !== vehicle.model ||
      (watchedValues.type ?? null) !== (vehicle.type ?? null) ||
      watchedValues.year_start !== vehicle.year_start ||
      watchedValues.year_end !== vehicle.year_end
    );
  }, [watchedValues, vehicle]);

  const carMakerListQuery = useQuery({
    queryKey: ["carMakerList"],
    queryFn: async () => {
      const res = await axios.get<CarMakerListResponse>(
        `${BASE_URL}/vehicle_types_manufacturer`
      );
      let list = res.data.content.map((value: any) => ({
        value: value,
        label: value,
      }));
      setMakerList(list);
      return list;
    },
  });

  const carModelListQuery = useQuery({
    queryKey: ["carModelList"],
    queryFn: async () => {
      const res = await axios.get<CarMakerListResponse>(
        `${BASE_URL}/vehicle_models_for_manufacturer/${form.getFieldValue(
          "manufacturer"
        )}`
      );
      let list = res.data.content.map((value: any) => ({
        value: value,
        label: value,
      }));
      setModelList(list);
      return list;
    },
    enabled: false,
  });

  useEffect(() => {
    if (!showModal || !vehicle) return;

    form.setFieldsValue(vehicle);

    setTimeout(() => {
      carMakerListQuery.refetch();
      carModelListQuery.refetch();
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, vehicle]);

  const queryClient = useQueryClient();

  const onFinish = (values: VehicleItem) => {
    values.id = vehicle.id;
    console.log("Received values of form: ", values);

    updateVehicleMutation.mutate(values, {
      onSuccess: () => onClose(),
      onError: (error) => {
        api.error({
          title: "Error editing vehicle",
          description: error.message,
          placement: "bottomRight",
          showProgress: true
        });
      },
    });
  };

  const updateVehicleMutation = useMutation({
    mutationFn: (values: VehicleItem) => {
      return axios.post(`${BASE_URL}/update_vehicle`, values, {
        headers: {
          // Overwrite Axios's automatically set Content-Type
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () =>{
      queryClient.invalidateQueries({ queryKey: ["vehicleList"] });
      api.success({
        title: "Success",
        description: "Vehicle updated successfully",
        placement: "bottomRight",
        showProgress: true
      });
    }
  });

  const getVehicleManufacturerNode = () => {
    return (
      <>
        <>Vehicle Manufacturer </>
        <Checkbox
          checked={manualManufacturer}
          onChange={() => {
            const value = !manualManufacturer;
            setManualManufacturer(value);
            setManualModel(value);
          }}
        >
          Manual
        </Checkbox>
      </>
    );
  };

  const getVehicleModelNode = () => {
    return (
      <>
        <>Vehicle Model</>
        <Checkbox
          checked={manualModel}
          onChange={() => setManualModel(!manualModel)}
        >
          Manual
        </Checkbox>
      </>
    );
  };

  const generateYearsArray = (startYear: number) => {
    const currentYear = new Date().getFullYear();
    const yearsArray = [];
    for (let year = startYear; year <= currentYear; year++) {
      yearsArray.push({ label: year.toString(), value: year.toString() });
    }
    return yearsArray;
  };

  const yearStart = Form.useWatch("year_start", form);
  const yearEnd = Form.useWatch("year_end", form);

  return (
    <Modal
      title={"Editing vehicle: " + vehicle.manufacturer + " " + vehicle.model}
      open={showModal}
      okText="Submit"
      cancelText="Cancel"
      okButtonProps={{
        autoFocus: false,
        htmlType: "submit",
        loading: updateVehicleMutation.isPending,
        disabled: !submittable || !isDirty || updateVehicleMutation.isPending,
      }}
      cancelButtonProps={{ disabled: updateVehicleMutation.isPending }}
      closable={!updateVehicleMutation.isPending}
      mask={{ closable: !updateVehicleMutation.isPending }}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      modalRender={(dom) => (
        <Form
          layout="vertical"
          form={form}
          name="form_edit_vehicle"
          onFinish={(values) => onFinish(values)}
        >
          {dom}
        </Form>
      )}
    >
      <Form.Item
        label={getVehicleManufacturerNode()}
        name="manufacturer"
        rules={[{ required: true }]}
      >
        {!manualManufacturer ? (
          <Select
            showSearch
            filterOption={(input, option) =>
              option?.label.toLowerCase().startsWith(input)
            }
            loading={carMakerListQuery.isLoading}
            options={makerList}
            onSelect={() => {
              carModelListQuery.refetch().then(() => {
                form.setFieldValue("model", null);
              });
            }}
          />
        ) : (
          <Input />
        )}
      </Form.Item>
      <Form.Item
        label={getVehicleModelNode()}
        name="model"
        rules={[{ required: true }]}
      >
        {!manualModel ? (
          <Select
            showSearch
            filterOption={(input, option) =>
              option?.label.toLowerCase().startsWith(input)
            }
            loading={carModelListQuery.isLoading}
            options={modelList}
          />
        ) : (
          <Input />
        )}
      </Form.Item>
      <Form.Item
        label="Type"
        name="type"
        rules={[{ required: false }]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="Year Range" required>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item
            name="year_start"
            rules={[{ required: true, message: 'Select start year' }]}
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Select
              placeholder="From"
              options={generateYearsArray(1986)}
              onChange={(value) => {
                if (!yearEnd || value > yearEnd) {
                  form.setFieldValue('year_end', value);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="year_end"
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Select
              placeholder="To"
              disabled={!yearStart}
              options={generateYearsArray(1986).filter(
                (y) => !yearStart || y.value >= yearStart
              )}
            />
          </Form.Item>
        </div>
      </Form.Item>
    </Modal>
  );
};
export default EditVehicleModal;
